import { yupResolver } from '@hookform/resolvers/yup';
import { Cancel, Person, Search } from '@mui/icons-material';
import { Box, Checkbox, Grid, Stack, styled } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IConsultant, ILocation } from 'wisestep-api';
import {
  WsColors,
  Item,
  SearchBar,
  WsButton,
  WsDetailsCard,
  WsTypography,
  WsDotIcon,
  WsDotColors,
  WsDotTextColors,
  WsTypoVariants,
  WsTypoColors,
  timeSince,
  WsIconPersons,
  WsIconVisa,
  WsPagination,
  WsLocationSearch,
} from 'wisestep-base';
import { array, object, string } from 'yup';

const SearchBarContainer = styled(Item)(({ theme }) => ({
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(0),
  paddingRigt: theme.spacing(0),
  width: '100%',
  boxShadow: '2px 2px 2px ' + WsColors.BorderGrey,
}));

const ConsultantSelectionFooter = styled(Item)(({ theme }) => ({
  position: 'fixed',
  padding: theme.spacing(2),
  backgroundColor: WsColors.White,
  borderTop: '1px solid' + WsColors.BorderGrey,
  bottom: 0,
  left: 911,
  right: 0,
}));

export interface IConsultantSearchFilter {
  consultantSearchQuery: string;
  locationSearchQuery: ILocation[];
}

export interface IConsultantSelectionProps {
  consultants: IConsultant[];
  totalCount: number;
  handleChangeParameters: (query: string, page: number) => Promise<void>;
  getSelectedConsultants: (arg: string[]) => void;
  handleConsultantSearch: (data: IConsultantSearchFilter) => Promise<void>;
  closeConsultantSelection: () => void;
}
const ConsultantSelection = ({
  consultants,
  totalCount,
  handleChangeParameters,
  getSelectedConsultants,
  handleConsultantSearch,
  closeConsultantSelection,
}: IConsultantSelectionProps) => {
  const getConsultantDetails = (consultant: IConsultant) => ({
    id: consultant.id,
    icon: <Person sx={{ color: WsColors.TheBlues500 }} />,
    title: consultant.name,
    locationsArray: consultant.locations,
    email: consultant.email,
    detailsArray: [
      {
        icon: <WsIconVisa />,
        labelName: `${consultant.visaStatus}, ${
          consultant.willingToRelocate ? 'Open to relocation' : 'Not open to relocation'
        }`,
      },
      { icon: <WsIconPersons />, labelName: consultant.jobTitle + ' @' + consultant.organization },
    ],
    rightChildren: (
      <WsDotIcon
        color={WsDotColors.GREEN}
        textColor={WsDotTextColors.GREEN}
        value={consultant.availability ?? 'Available'}
      />
    ),
    footer: (
      <Box px={2} pb={1}>
        <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.TERTIARY}>
          {`Created by ${consultant.createdByName} ${timeSince(
            consultant.createdOn?.toString() ?? ''
          )}`}
        </WsTypography>
      </Box>
    ),
  });

  const schema = object().shape({
    consultantSearchQuery: string(),
    locationSearchQuery: array(),
  });

  const defaultValues = {
    consultantSearchQuery: '',
    locationSearchQuery: [],
  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  });
  const [selectedConsultants, setSelectedConsultants] = useState<string[]>([]);

  const handleSelection = (consultantCode: string, checked: boolean) => {
    checked ? addConsultant(consultantCode) : removeConsultant(consultantCode);
  };
  const importConsultants = () => getSelectedConsultants(selectedConsultants);

  const addConsultant = (consultantCode: string) =>
    setSelectedConsultants((prev) => [...prev, consultantCode]);

  const removeConsultant = (consultantCode: string) =>
    setSelectedConsultants((prev) => prev.filter((code) => code !== consultantCode));

  return (
    <FormProvider {...methods}>
      <Stack
        p={2}
        spacing={2}
        component={'form'}
        onSubmit={methods.handleSubmit(handleConsultantSearch)}
      >
        <Stack direction={'row'} alignItems="center" spacing={1}>
          <SearchBarContainer>
            <SearchBar name="consultantSearchQuery" placeholder="Search by name..." />
          </SearchBarContainer>
          <Item sx={{ p: 0, width: '100%' }}>
            <WsLocationSearch
              hideLabel={true}
              placeHolder="Search by location..."
              name="locationSearchQuery"
            />
          </Item>
          <WsButton variant="outlined" size="large" startIcon={<Search />} type="submit">
            Search
          </WsButton>
        </Stack>
        <Stack direction={'row'} spacing={0.5} alignItems={'center'} justifyContent="space-between">
          <WsTypography>{`Showing ${
            totalCount > 10 ? 10 : totalCount
          } out of ${totalCount} results`}</WsTypography>
          <WsPagination
            count={Math.ceil(totalCount / 10)}
            onChange={(ev, page) => {
              handleChangeParameters(methods.getValues('consultantSearchQuery'), page - 1);
            }}
          />
        </Stack>
      </Stack>
      <Stack p={2} mb={6} spacing={2}>
        {consultants.map((consultant: IConsultant, i: number) => (
          <Grid container key={i}>
            <Grid item xs={0.8}>
              <Checkbox
                checked={selectedConsultants.includes(consultant.id ?? '')}
                onChange={(ev) => handleSelection(consultant.id ?? '', ev.target.checked)}
              />
            </Grid>
            <Grid item xs={11.2}>
              <WsDetailsCard gridXs={6} {...getConsultantDetails(consultant)} />
            </Grid>
          </Grid>
        ))}
      </Stack>
      <ConsultantSelectionFooter>
        <Stack direction={'row'} justifyContent={'flex-end'} spacing={2}>
          <WsButton
            size="large"
            variant="outlined"
            startIcon={<Cancel />}
            onClick={closeConsultantSelection}
          >
            Cancel
          </WsButton>
          <WsButton
            size="large"
            onClick={importConsultants}
            disabled={selectedConsultants.length === 0}
          >
            {'Selected ' + selectedConsultants.length + ' Consultant(s)'}
          </WsButton>
        </Stack>
      </ConsultantSelectionFooter>
    </FormProvider>
  );
};

export default ConsultantSelection;
